import { useState } from "react";
import { Alert, Button, Layout, Menu, Spin, Tag, Typography } from "@arco-design/web-react";
import { HashRouter, Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useBaseContext, type BaseContext } from "./bootstrap/useBaseContext.js";
import { useHandshake } from "./bootstrap/useHandshake.js";
import { SetupWizard } from "./bootstrap/SetupWizard.js";
import { DashboardPage } from "./pages/Dashboard.js";
import { ShopsPage } from "./pages/Shops.js";
import { SettingsPage } from "./pages/Settings.js";
import { EventsPage } from "./pages/Events.js";
import { useAuthStore } from "./store/authStore.js";

const { Sider, Header, Content } = Layout;
const { Title, Text } = Typography;

const MENU_ITEMS = [
  { key: "/dashboard", label: "运行概况" },
  { key: "/shops", label: "店铺与凭证" },
  { key: "/events", label: "事件队列" },
  { key: "/settings", label: "设置" },
];

function ShellChrome(): JSX.Element {
  const auth = useAuthStore();
  const location = useLocation();
  return (
    <Layout style={{ height: "100%" }}>
      <Header style={{ background: "#fff", padding: "0 16px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        <div className="os-row" style={{ justifyContent: "space-between" }}>
          <div className="os-row">
            <Title heading={6} style={{ margin: 0 }}>
              多平台订单同步
            </Title>
            <Tag color="arcoblue">{auth.displayName ?? "—"}</Tag>
          </div>
          <Text type="secondary">install_id：{auth.installId ?? "—"}</Text>
        </div>
      </Header>
      <Layout>
        <Sider width={180} theme="light">
          <Menu selectedKeys={[location.pathname]} style={{ height: "100%" }}>
            {MENU_ITEMS.map((m) => (
              <Menu.Item key={m.key}>
                <Link to={m.key}>{m.label}</Link>
              </Menu.Item>
            ))}
          </Menu>
        </Sider>
        <Content style={{ padding: 16, overflowY: "auto" }}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/shops" element={<ShopsPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

export function App(): JSX.Element {
  const baseCtx = useBaseContext();
  const [setupDone, setSetupDone] = useState(0);

  if (baseCtx.phase === "loading") {
    return (
      <div className="os-shell">
        <Spin tip="正在加载飞书插件上下文…" />
      </div>
    );
  }
  if (baseCtx.phase === "error") {
    return (
      <div className="os-shell">
        <Alert
          type="error"
          content={
            <div>
              <Text>读取飞书表格上下文失败：{baseCtx.message}</Text>
              <br />
              <Text type="secondary">
                如果你是在浏览器里直接打开本地 dev URL，请通过飞书表格的「自定义插件 → 输入 URL」来加载本插件。
              </Text>
            </div>
          }
        />
      </div>
    );
  }
  return <Bootstrap baseCtx={baseCtx.context} key={setupDone} onSetupDone={() => setSetupDone((n) => n + 1)} />;
}

function Bootstrap(props: { baseCtx: BaseContext; onSetupDone: () => void }): JSX.Element {
  const status = useHandshake(props.baseCtx);
  if (status.phase === "loading") {
    return (
      <div className="os-shell">
        <Spin tip="正在与服务端 handshake…" />
      </div>
    );
  }
  if (status.phase === "error") {
    return (
      <div className="os-shell">
        <Alert
          type="error"
          content={`handshake 失败：${status.message}`}
          action={
            <Button size="mini" onClick={() => window.location.reload()}>
              重试
            </Button>
          }
        />
      </div>
    );
  }
  if (status.phase === "needs_setup") {
    return (
      <div className="os-shell">
        <SetupWizard onDone={props.onSetupDone} />
      </div>
    );
  }
  // ready
  return (
    <HashRouter>
      <ShellChrome />
    </HashRouter>
  );
}
